import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import GalleryLightbox from '../components/GalleryLightbox';
import { Instagram, Play } from 'lucide-react';
import { isVideoMedia } from '../utils/media';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/gallery`);
        setImages(response.data);
      } catch (error) {
        console.error('Error fetching gallery:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div data-testid="gallery-page" className="bg-[#0A0A0A] min-h-screen pt-20">
      {/* Hero */}
      <section className="py-24 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Oswald'] text-5xl sm:text-6xl md:text-7xl text-[#F2EDE6] mb-4"
          >
            THE ROAD BEHIND US
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[#A19D98] text-lg"
          >
            Every rally. Every mile. Every memory.
          </motion.p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-[#A19D98]">Loading gallery...</p>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#A19D98]">No images yet. Check back after our next rally!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {images.map((image, index) => {
                const isVideo = isVideoMedia(image.url, image.media_type);
                return (
                <motion.div
                  key={image.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="relative group cursor-pointer aspect-[4/3] overflow-hidden"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-image-${index}`}
                >
                  {isVideo ? (
                    <video
                      src={image.url}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />
                  ) : (
                    <img
                      src={image.url}
                      alt={image.alt || `Gallery image ${index + 1}`}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-center justify-center">
                    <span className="font-['Oswald'] text-[#F2EDE6] tracking-wider opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {image.rally_name || 'VIEW'}
                    </span>
                  </div>
                  {isVideo && (
                    <div className="absolute top-2 right-2 bg-black/70 text-[#F2EDE6] p-1.5 rounded-full">
                      <Play size={12} fill="currentColor" />
                    </div>
                  )}
                </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-[#141414]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6] mb-4">
              WERE YOU THERE?
            </h2>
            <p className="text-[#A19D98] mb-8">
              Tag us on Instagram @revlinerally
            </p>
            <a
              href="https://www.instagram.com/revlinerally/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="gallery-instagram-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Instagram size={18} />
              @revlinerally
            </a>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <GalleryLightbox
        images={images}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default Gallery;
